import { RmsBadgeTemplateFormSettings } from '@/backend/features/badging/badging-configuration/types';
import { BadgeCreateTemplateSettingsForm } from '@juvare/badging/ui/features/badge-management/components/BadgeCreateWIzard/BadgeCreateTemplateSettingsForm';
import { BadgeCreateWizardStepContainer } from '@juvare/badging/ui/features/badge-management/components/BadgeCreateWIzard/BadgeCreateWizardStepContainer';
import { BadgeOrganizationsSelection } from '@juvare/badging/ui/features/badge-management/components/BadgeCreateWIzard/BadgeOrganizationsSelection';
import { BadgeDetailsFormConfigData } from '@juvare/badging/ui/features/badge-management/utils/getBadgeDetailsFormConfig';
import { FormRef } from '@juvare/common-form-ui/Form';
import { WizardStepComponentProps, WizardStepComponentRef } from '@juvare/common-form-ui/Wizard';
import { Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCreateWizardData, BadgeCreateWizardStep2Data } from './types';

export const RmsBadgeCreateWizardStep2 = React.forwardRef<WizardStepComponentRef, WizardStepComponentProps>((props, ref) => {
    const { t } = useTranslation('rms');

    const data = (props.data ?? {}) as BadgeCreateWizardData;
    const [formSettings, setFormSettings] = useState<RmsBadgeTemplateFormSettings>(
        data.formSettings ?? ((data.badgeTemplate?.data.defaultFormSettings ?? {}) as RmsBadgeTemplateFormSettings),
    );

    const refBadgeCreateTemplateSettingsForm = React.useRef<FormRef<BadgeDetailsFormConfigData>>(null);

    const [organizationsAdminsCanAssignBadges, setOrganizationsAdminsCanAssignBadges] = useState<string[] | undefined>(
        data.organizationsAdminsCanAssignBadges,
    );
    const organizationsAdminsCanAssignBadgesChecked = Boolean(organizationsAdminsCanAssignBadges);
    const handleOrganizationsToggle = () => setOrganizationsAdminsCanAssignBadges((current) => (current ? undefined : []));
    const handleSelectOrganizations = (ids: string[]) => {
        setOrganizationsAdminsCanAssignBadges(ids);
    };

    React.useImperativeHandle(ref, () => ({
        getData: (): BadgeCreateWizardStep2Data => {
            return {
                formSettings,
                organizationsAdminsCanAssignBadges: organizationsAdminsCanAssignBadges,
            };
        },
        submit: async () => {
            const dataNew: BadgeCreateWizardStep2Data = {
                formSettings,
                organizationsAdminsCanAssignBadges: organizationsAdminsCanAssignBadges,
            };

            try {
                // force form validation
                await refBadgeCreateTemplateSettingsForm.current?.submit();
            } catch {
                return;
            }

            return Promise.resolve(
                props.onSubmit(dataNew).then((response) => {
                    props.onSubmitSuccess(dataNew, response);
                }),
            );
        },
    }));

    if (!data.badgeTemplate || !data.badgeTemplate.data.form) {
        return null;
    }

    return (
        <BadgeCreateWizardStepContainer
            headerText={t('badging.create.wizard.step2.contentTitle')}
            badgeTemplateSelected={data.badgeTemplate}
            formSettings={formSettings}
            fixedBadgePreviewPosition={true}
        >
            <BadgeCreateTemplateSettingsForm
                formRef={refBadgeCreateTemplateSettingsForm}
                badgeTemplate={data.badgeTemplate}
                state={formSettings}
                setState={setFormSettings}
            />

            <Stack mt={2} gap={2}>
                <BadgeOrganizationsSelection
                    enableSelectionChecked={organizationsAdminsCanAssignBadgesChecked}
                    onEnableSelectionCheckedToggle={handleOrganizationsToggle}
                    organizationIdsSelected={organizationsAdminsCanAssignBadges ?? []}
                    onChangeOrganizationIds={handleSelectOrganizations}
                />
            </Stack>
        </BadgeCreateWizardStepContainer>
    );
});
